/**
 * WorkspaceService - API client for workspace registry and user preferences.
 */

const API_BASE = '/api/v1/workspaces';

class WorkspaceService {
  static getAuthHeaders() {
    const token = localStorage.getItem('waide_access_token');
    return {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  static async handleResponse(response) {
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:unauthorized', {
        detail: { message: 'Session expired' },
      }));
      throw new Error('Unauthorized');
    }
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || `HTTP error ${response.status}`);
    }
    return response.json();
  }

  /**
   * Get workspaces the current user can access (role-checked).
   */
  static async getAvailableWorkspaces() {
    const response = await fetch(`${API_BASE}/available`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get workspace JSON definition by code.
   */
  static async getWorkspaceDefinition(workspaceCode) {
    const response = await fetch(`${API_BASE}/${workspaceCode}/definition`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get workspace group labels (maps domain codes to display names).
   */
  /** Where the RF app lives (a backend setting), for Warehouse Operations > RF App. '' when not set. */
  static async getRfAppUrl() {
    try {
      const response = await fetch('/api/v1/mobile/app-link', { headers: this.getAuthHeaders() });
      if (!response.ok) return '';
      return (await response.json()).url || '';
    } catch (e) {
      return '';
    }
  }

  static async getWorkspaceGroups() {
    const response = await fetch(`${API_BASE}/groups`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Get user's pinned workspace menu items.
   */
  static async getPinnedWorkspaces() {
    const response = await fetch(`${API_BASE}/pinned`, {
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Pin a workspace (or, with viewId, one specific view/tab inside a
   * multi-view workspace) to user's menu.
   */
  static async pinWorkspace(workspaceType, customLabel, viewId = null) {
    const response = await fetch(`${API_BASE}/pin`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ workspaceType, customLabel, viewId }),
    });
    return this.handleResponse(response);
  }

  /**
   * Unpin a workspace (or one specific view/tab, if viewId is given) from
   * user's menu.
   */
  static async unpinWorkspace(workspaceType, viewId = null) {
    const qs = viewId ? `?viewId=${encodeURIComponent(viewId)}` : '';
    const response = await fetch(`${API_BASE}/pin/${workspaceType}${qs}`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });
    return this.handleResponse(response);
  }

  /**
   * Reorder pinned workspaces/tabs. `pins` is a list of
   * {workspaceType, viewId} (viewId null for a whole-workspace pin) in the
   * desired order.
   */
  static async reorderWorkspaces(pins) {
    const response = await fetch(`${API_BASE}/pin/reorder`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ pins }),
    });
    return this.handleResponse(response);
  }
}

export default WorkspaceService;
